import {
  evaluateTerms,
  parseTextTemplate,
  type ReportNumberFormatInput,
  type ReportValueFormat,
  type TextTemplate,
  type TextValue,
} from '@erp/shared-types';
import { formatCurrency } from '@/components/common/Currency';
import type {
  ReportDataset,
  ReportDatasetColumn,
  ReportDatasetRow,
} from '../ReportDataContext';

// ── Values and `{placeholder}` text, as a Report shows them ──────────────────
// A component's text (title, description, notes, a KPI's value, a Table's
// text cells, a Text block) can show values of the dataset it is bound to.
// Each value reads in its dataset column's format, which the surface states
// from its own catalog; the canvas knows no catalog.

/** The value of `path` in `row`: a flat key, else a dotted path into nested objects. */
export function readField(row: unknown, path: string): unknown {
  if (row == null || typeof row !== 'object') return undefined;
  if (path in row) return (row as Record<string, unknown>)[path];
  let value: unknown = row;
  for (const key of path.split('.')) {
    if (value == null || typeof value !== 'object') return undefined;
    value = (value as Record<string, unknown>)[key];
  }
  return value;
}

function dayToDate(day: string): Date {
  return new Date(`${day}T00:00:00Z`);
}

const DASH = '—';

// Intl formatters are slow to build; one per locale and style, built once.
const NUMBER_STYLES = {
  percent: { style: 'percent', maximumFractionDigits: 0 },
  days: { maximumFractionDigits: 1 },
  count: { maximumFractionDigits: 0 },
  number: {},
} as const satisfies Record<string, Intl.NumberFormatOptions>;
const DATE_STYLES = {
  day: { dateStyle: 'medium', timeZone: 'UTC' },
  date: { dateStyle: 'medium' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;
const numberFormats = new Map<string, Intl.NumberFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();
const pluralRules = new Map<string, Intl.PluralRules>();

function cached<T>(cache: Map<string, T>, key: string, make: () => T): T {
  const hit = cache.get(key);
  if (hit) return hit;
  const made = make();
  cache.set(key, made);
  return made;
}

function numberFormat(locale: string, style: keyof typeof NUMBER_STYLES) {
  return cached(
    numberFormats,
    `${locale}:${style}`,
    () => new Intl.NumberFormat(locale, NUMBER_STYLES[style]),
  );
}

function dateFormat(locale: string, style: keyof typeof DATE_STYLES) {
  return cached(
    dateFormats,
    `${locale}:${style}`,
    () => new Intl.DateTimeFormat(locale, DATE_STYLES[style]),
  );
}

function pluralRulesFor(locale: string) {
  return cached(pluralRules, locale, () => new Intl.PluralRules(locale));
}

function plainText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  return JSON.stringify(value);
}

/**
 * One value as text in `locale`, as `format` reads it (see
 * `ReportValueFormat`): counts grouped, money in dollars, shares as whole
 * percents, days to one decimal, dates in the medium style. Empty or
 * unreadable reads as a dash; without a format, as written.
 */
export function formatReportValue(
  value: unknown,
  format: ReportValueFormat | null | undefined,
  locale: string,
): string {
  if (value == null || value === '') return DASH;
  if (format === 'day' || format === 'date') {
    if (typeof value !== 'string') return DASH;
    const date = format === 'day' ? dayToDate(value) : new Date(value);
    if (Number.isNaN(date.getTime())) return DASH;
    return dateFormat(locale, format).format(date);
  }
  if (format == null || format === 'text') return plainText(value);
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return DASH;
  switch (format) {
    case 'money':
      return formatCurrency(number, { currency: 'USD', locale }) || DASH;
    case 'percent':
      return numberFormat(locale, 'percent').format(number);
    case 'percent100':
      return numberFormat(locale, 'percent').format(number / 100);
    case 'days':
    case 'count':
    case 'number':
      return numberFormat(locale, format).format(number);
  }
}

/** A dataset column's format by key; null when it states none. */
export function columnFormat(
  columns: readonly ReportDatasetColumn[] | undefined,
  key: string,
): ReportValueFormat | null {
  return columns?.find((column) => column.key === key)?.format ?? null;
}

/**
 * A column's stated format as a Report Number format, for a component that
 * formats numbers itself (a KPI's headline); undefined where it has none.
 */
export function reportNumberFormatFor(
  format: ReportValueFormat,
): ReportNumberFormatInput | undefined {
  switch (format) {
    case 'money':
      return { style: 'currency' };
    case 'percent':
      return { style: 'percent', decimals: 0 };
    case 'count':
      return { decimals: 0 };
    case 'days':
      return { decimals: 1 };
    default:
      return undefined;
  }
}

/** What a template reads its values from, and how to show each. */
export interface TextContext {
  /** A value by name; undefined when nothing holds that name. */
  read: (name: string) => unknown;
  formatOf: (name: string) => ReportValueFormat | null;
  locale: string;
}

/** The placeholder as written, for one that reads a name nothing holds. */
function asWritten(part: Exclude<TextTemplate[number], { kind: 'text' }>) {
  const terms = part.terms
    .map((term, index) =>
      index === 0 && term.sign === 1
        ? term.name
        : `${term.sign === 1 ? '+' : '-'} ${term.name}`,
    )
    .join(' ');
  return part.kind === 'plural'
    ? `{${terms}, plural, one {${part.one}} other {${part.other}}}`
    : `{${terms}}`;
}

function renderTemplate(template: TextTemplate, context: TextContext): string {
  const plural = pluralRulesFor(context.locale);
  return template
    .map((part) => {
      if (part.kind === 'text') return part.text;
      // A name nothing holds (a typo, or text on a surface without that
      // value) shows as written rather than as an empty value.
      if (part.terms.some((term) => context.read(term.name) === undefined))
        return asWritten(part);
      if (part.kind === 'plural') {
        const value = evaluateTerms(part.terms, context.read);
        return value != null && plural.select(value) === 'one'
          ? part.one
          : part.other;
      }
      const [only] = part.terms;
      if (part.terms.length === 1 && only) {
        return formatReportValue(
          context.read(only.name),
          context.formatOf(only.name),
          context.locale,
        );
      }
      const format = only ? context.formatOf(only.name) : null;
      return formatReportValue(
        evaluateTerms(part.terms, context.read),
        format ?? 'count',
        context.locale,
      );
    })
    .join('');
}

/** `text` with its placeholders filled; the text as written when it doesn't parse. */
export function formatReportText(text: string, context: TextContext): string {
  const parsed = parseTextTemplate(text);
  return parsed.ok ? renderTemplate(parsed.template, context) : text;
}

/**
 * The sum of the values a template reads, as a note's condition tests it;
 * null when any is missing or empty.
 */
export function templateValue(
  text: string,
  context: Pick<TextContext, 'read'>,
): TextValue {
  const parsed = parseTextTemplate(text);
  if (!parsed.ok) return null;
  const terms = parsed.template.flatMap((part) =>
    part.kind === 'text' ? [] : part.terms,
  );
  return terms.length === 0 ? null : evaluateTerms(terms, context.read);
}

/**
 * What a component's text reads: its text dataset's totals and its own
 * dataset's (its own win), and `rows` (shown) and `total` (in the whole
 * source) of its dataset. Each value reads in its column's format.
 */
export function datasetTextContext(
  dataset: ReportDataset | null,
  textDataset: ReportDataset | null,
  locale: string,
): TextContext {
  const values: Record<string, unknown> = {
    ...(dataset && {
      rows: dataset.rows.length,
      total: dataset.totalRows ?? dataset.rows.length,
    }),
    ...textDataset?.totals,
    ...dataset?.totals,
  };
  return {
    locale,
    read: (name) => values[name],
    formatOf: (name) =>
      name === 'rows' || name === 'total'
        ? 'count'
        : (columnFormat(dataset?.columns, name) ??
          columnFormat(textDataset?.columns, name)),
  };
}

/** What a Table cell's text reads: the row's fields, each in its column's format. */
export function rowTextContext(
  row: ReportDatasetRow,
  columns: readonly ReportDatasetColumn[],
  locale: string,
): TextContext {
  return {
    locale,
    read: (name) => readField(row, name),
    formatOf: (name) => columnFormat(columns, name) ?? 'count',
  };
}

/**
 * An app path with its `{field}` placeholders filled from `row`, each value
 * URL-encoded; null when a field it names is empty (no link to draw).
 */
export function fillPath(path: string, row: ReportDatasetRow): string | null {
  let missing = false;
  const filled = path.replace(/\{([A-Za-z_][A-Za-z0-9_.]*)\}/g, (_m, name) => {
    const value = readField(row, String(name));
    if (value == null || value === '') {
      missing = true;
      return '';
    }
    return encodeURIComponent(plainText(value));
  });
  return missing ? null : filled;
}
